import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import { Box, Card, CardContent, CardMedia, Container, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';

import { MedirVisita } from '@/components/MedirVisita';

import type { Route } from './+types/sobre-nosotros';

const HISTORIA = [
  {
    anio: '2009',
    titulo: 'Fundación',
    texto:
      'CKING inicia operaciones como una pequeña empresa importadora de repuestos para ' +
      'motocicletas en Argentina.',
  },
  {
    anio: '2012',
    titulo: 'Expansión',
    texto:
      'Ampliamos nuestra cartera de productos y comenzamos a trabajar con las principales ' +
      'marcas del mercado.',
  },
  {
    anio: '2015',
    titulo: 'Crecimiento',
    texto:
      'Inauguramos nuestro centro de distribución principal y expandimos nuestra red de ' +
      'distribuidores.',
  },
  {
    anio: '2018',
    titulo: 'Innovación',
    texto:
      'Implementamos sistemas logísticos avanzados para mejorar la eficiencia en la ' +
      'distribución de nuestros productos.',
  },
  {
    anio: '2021',
    titulo: 'Liderazgo',
    texto:
      'Nos consolidamos como líderes en el mercado de importación de repuestos para ' +
      'motocicletas en Argentina.',
  },
  {
    anio: 'Hoy',
    titulo: 'Presente y Futuro',
    texto:
      'Continuamos creciendo y mejorando nuestros servicios para satisfacer las necesidades de ' +
      'nuestros clientes.',
  },
];

const VALORES = [
  {
    titulo: 'Calidad',
    texto:
      'Nos comprometemos a ofrecer productos de la más alta calidad para garantizar la ' +
      'satisfacción de nuestros clientes.',
    imagen: '/img/sobre-nosotros/calidad.webp',
  },
  {
    titulo: 'Innovación',
    texto:
      'Buscamos constantemente nuevas soluciones y productos para mantenernos a la vanguardia ' +
      'del mercado.',
    imagen: '/img/sobre-nosotros/innovacion.webp',
  },
  {
    titulo: 'Servicio',
    texto:
      'Brindamos un servicio excepcional, centrado en las necesidades y expectativas de ' +
      'nuestros clientes.',
    imagen: '/img/sobre-nosotros/servicio.webp',
  },
  {
    titulo: 'Compromiso',
    texto:
      'Estamos comprometidos con el desarrollo del sector y la satisfacción de nuestros ' +
      'clientes y colaboradores.',
    imagen: '/img/sobre-nosotros/compromiso.webp',
  },
];

const SUBRAYADO = {
  content: '""',
  position: 'absolute',
  width: '60px',
  height: '3px',
  bottom: '-10px',
  left: '50%',
  backgroundColor: 'primary.main',
  transform: 'translateX(-50%)',
} as const;

export function meta(): Route.MetaDescriptors {
  return [
    { title: 'Sobre Nosotros | CKING Repuestos para Motos' },
    {
      name: 'description',
      content:
        'CKING importa y comercializa repuestos para motocicletas en Argentina desde 2009. ' +
        'Nuestra historia y nuestros valores.',
    },
    { tagName: 'link', rel: 'canonical', href: 'https://cking.com.ar/sobre-nosotros' },
  ];
}

export default function SobreNosotros() {
  return (
    <>
      <MedirVisita path="/sobre-nosotros" />

      <Box
        sx={{
          py: 8,
          bgcolor: '#0a0a0a',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/img/sobre-nosotros/sobre-nosotros-800.webp)',
            '@media (min-width: 900px)': {
              backgroundImage: 'url(/img/sobre-nosotros/sobre-nosotros-1600.webp)',
            },
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.5,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Los `size` no estaban. Sin ellos el Grid de MUI v9 no reparte la fila: los dos
              bloques se apilaban y el logo terminaba ocupando el ancho completo debajo del
              texto, en vez de al lado. */}
          <Grid container spacing={6} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{ mb: 3, fontWeight: 700, textTransform: 'uppercase' }}
                >
                  Sobre Nosotros
                </Typography>

                <Typography
                  variant="h5"
                  component="p"
                  sx={{ mb: 4, color: 'rgba(255, 255, 255, 0.8)' }}
                >
                  CKING es una empresa argentina que desde 2009 está dedicada a la importación y la
                  comercialización de repuestos para motocicletas de primera calidad.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Estamos en la permanente investigación de las necesidades del mercado y la
                  satisfacción del cliente y consumidor, brindamos productos de excelente calidad,
                  seguridad y precio.
                </Typography>

                <Typography variant="body1">
                  Nuestro compromiso es ofrecer soluciones integrales para el sector de las
                  motocicletas, trabajando con las mejores marcas a nivel mundial.
                </Typography>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  component="img"
                  src="/img/sobre-nosotros/logo-cking.webp"
                  alt="Sobre CKING"
                  // El borderRadius sobre un PNG con fondo transparente no recortaba nada y
                  // dejaba a la vista una esquina clara del archivo. El logo no necesita marco.
                  sx={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 700,
              position: 'relative',
              display: 'inline-block',
              left: '50%',
              transform: 'translateX(-50%)',
              '&::after': SUBRAYADO,
            }}
          >
            Nuestra Historia
          </Typography>
        </motion.div>

        <Timeline position="alternate">
          {HISTORIA.map((hito, indice) => (
            <TimelineItem key={hito.anio}>
              <TimelineSeparator>
                <TimelineDot color="primary" />
                {indice < HISTORIA.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: indice * 0.1 }}
                >
                  <Paper
                    elevation={3}
                    sx={{ p: 3, bgcolor: '#1a1a1a', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                  >
                    <Typography variant="h6" component="p" color="primary" sx={{ fontWeight: 700 }}>
                      {hito.anio}
                    </Typography>

                    <Typography variant="h5" component="h3" sx={{ my: 1, fontWeight: 600 }}>
                      {hito.titulo}
                    </Typography>

                    <Typography>{hito.texto}</Typography>
                  </Paper>
                </motion.div>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Container>

      <Box sx={{ py: 8, bgcolor: '#0a0a0a' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h3"
              component="h2"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 700,
                position: 'relative',
                display: 'inline-block',
                left: '50%',
                transform: 'translateX(-50%)',
                '&::after': SUBRAYADO,
              }}
            >
              Nuestros Valores
            </Typography>
          </motion.div>

          <Grid container spacing={4} sx={{ justifyContent: 'center' }}>
            {VALORES.map((valor, indice) => (
              <Grid key={valor.titulo}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: indice * 0.1 }}
                >
                  <Card
                    sx={{
                      width: { xs: 300, sm: 350, md: 400, lg: 500 },
                      height: { xs: 300, sm: 350, md: 400, lg: 500 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      p: 3,
                      boxShadow: 3,
                      border: '1px solid rgba(255,255,255,0.05)',
                      textAlign: 'center',
                    }}
                  >
                    <CardMedia
                      component="img"
                      src={valor.imagen}
                      alt={valor.titulo}
                      sx={{
                        width: { xs: 100, sm: 150, md: 200, lg: 250 },
                        height: { xs: 100, sm: 150, md: 200, lg: 250 },
                        objectFit: 'contain',
                      }}
                    />

                    <CardContent>
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 2 }}>
                        {valor.titulo}
                      </Typography>

                      <Typography variant="body1">{valor.texto}</Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}
